// LLM generated - Model Claude 4 Sonnet
import { useState, useRef, useEffect } from 'react';
import { Stack } from '@mantine/core';
import { IconArrowUp } from '@tabler/icons-react';
import useTranslation from 'next-translate/useTranslation';
import { showNotification } from '@mantine/notifications';

import { Button } from '../../../Button';
import { Text } from '../../../Text';
import useStyles from './ChatFooter.styles';

const MAX_MESSAGE_LENGTH = 300;
const WARNING_THRESHOLD = 25;

interface ChatFooterProps {
  onSubmit?: (content: string) => void;
  onAddMedia?: () => void;
  onOpenPostModal?: () => void; // New prop for opening post modal
  placeholder?: string;
  disabled?: boolean;
  variant?: 'input' | 'post-button'; // New prop to control footer type
  autoFocus?: boolean; // New prop to auto-focus the input when component mounts
  value?: string; // Controlled input value (for persisting draft messages)
  onChange?: (value: string) => void; // Callback when input changes
}

function ChatFooter({
  onSubmit,
  onAddMedia,
  onOpenPostModal,
  placeholder,
  disabled = false,
  variant = 'input',
  autoFocus = false,
  value: controlledValue,
  onChange: onControlledChange,
}: ChatFooterProps) {
  const { t } = useTranslation('chat');
  const { classes, cx } = useStyles();
  // Use controlled value if provided, otherwise use internal state
  const [internalValue, setInternalValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const inputValue = controlledValue !== undefined ? controlledValue : internalValue;
  const inputRef = useRef<HTMLInputElement>(null);

  const remainingChars = MAX_MESSAGE_LENGTH - inputValue.length;
  const isNearLimit = remainingChars <= WARNING_THRESHOLD;

  // Use localized placeholder as default
  const displayPlaceholder = placeholder || t('placeholders.post');

  // Auto-focus the input when component mounts if autoFocus is true and variant is 'input'
  // Also refocus when switching back to this component (e.g., tab change)
  useEffect(() => {
    if (autoFocus && variant === 'input' && !disabled) {
      // Small delay to ensure the modal/drawer is fully rendered
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [autoFocus, variant, disabled]);

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault(); // Prevent any form submission

    if (inputValue.trim() && onSubmit) {
      onSubmit(inputValue.trim());
      // Clear the input value (controlled or uncontrolled)
      if (onControlledChange) {
        onControlledChange('');
      } else {
        setInternalValue('');
      }

      // Re-focus the input field after sending
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    if (newValue.length > MAX_MESSAGE_LENGTH) {
      showNotification({
        id: 'chat-max-length-exceeded',
        message: t('notifications.maxCharacterLimitExceeded'),
        color: 'red',
      });
      return;
    }

    // Update value (controlled or uncontrolled)
    if (onControlledChange) {
      onControlledChange(newValue);
    } else {
      setInternalValue(newValue);
    }
  };

  const handleFocus = () => setIsFocused(true);
  const handleBlur = () => setIsFocused(false);

  return (
    <form onSubmit={handleSubmit} className={classes.container}>
      {variant === 'post-button' ? (
        // Show "Send a post" button for commissioners on posts tab
        <div className={classes.postButtonContainer}>
          <Button
            className={classes.createPostButton}
            variant="secondary-fill"
            size="medium"
            fullWidth
            onClick={() => {
              onOpenPostModal?.();
            }}
            disabled={disabled}
          >
            {t('post.createPostCTA')}
          </Button>
        </div>
      ) : (
        <div className={classes.inputWrapper}>
          <div className={classes.inputField}>
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              onFocus={handleFocus}
              onBlur={handleBlur}
              placeholder={displayPlaceholder}
              disabled={disabled}
              maxLength={MAX_MESSAGE_LENGTH}
              className={classes.input}
            />
            <Stack /* eslint-disable-line local-rules/use-theme-tokens */ spacing={4} align="center">
              <Text
                variant="body-small"
                color={isNearLimit ? 'red.3' : 'gray2.3'}
                className={cx(classes.characterCounter, {
                  [classes.characterCounterVisible]: isFocused,
                })}
              >
                {remainingChars}
              </Text>
              <Button
                type="submit"
                variant="tertiary-fill"
                size="small-icon"
                disabled={disabled || !inputValue.trim()}
                aria-label={t('ariaLabels.sendPost')}
                className={classes.sendButton}
              >
                <IconArrowUp size={16} color="white" />
              </Button>
            </Stack>
          </div>
        </div>
      )}
    </form>
  );
}

export default ChatFooter;
